import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, Calendar, Briefcase, FileSpreadsheet, ArrowUpDown, Search,
  Users, Trash2, Edit2, ChevronRight, ChevronLeft, User, X, CheckCircle, Info, FileText, Download, Zap, Building2, UserPlus, ToggleLeft, ToggleRight, Hash, Award, Database
} from 'lucide-react';
import axios from 'axios';
import './PostedJobs.css';
import TriggerAssessmentButton from './TriggerAssessmentButton';
import AssessmentReportModal from './AssessmentReportModal';
import AssessmentComparisonModal from './AssessmentComparisonModal';
import AssessmentPreviewModal from './AssessmentPreviewModal';
import ExistingCandidatesModal from './ExistingCandidatesModal';
import BeelineSubmissionForm from '../Vendorfiles/BeelineSubmissionForm';
import { downloadAssessmentReport } from './downloadAssessmentReport';

export default function PostedJobs({ onEdit, onDelete, onViewBeeline, externalShowBeeline, setExternalShowBeeline, partnerId }) { 
  // --- STATES ---
  const [viewState, setViewState] = useState('LIST'); 
  const [activeTab, setActiveTab] = useState('open');
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [viewingSubmissionId, setViewingSubmissionId] = useState(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showExisting, setShowExisting] = useState(false);
    const [adminPrefill, setAdminPrefill] = useState(null);

  // --- BEELINE TOGGLE STATE (LIFTED LOGIC) ---
  // If props are provided (from AdminDashboard), use them. Otherwise fallback to local state.
  const [localShowBeeline, setLocalShowBeeline] = useState(false);
  
  const isBeelineMode = externalShowBeeline !== undefined ? externalShowBeeline : localShowBeeline;
  
  const toggleBeeline = () => {
      if (setExternalShowBeeline) {
          setExternalShowBeeline(!externalShowBeeline);
      } else {
          setLocalShowBeeline(!localShowBeeline);
      }
  };

  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ vendors: [], recruiters: [], candidatePortal: [] });
  const [allCandidates, setAllCandidates] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [isMatching, setIsMatching] = useState(false);

  // Sorting
  const [sortOrder, setSortOrder] = useState('match_desc');
  
  // Search State
  const [searchTerm, setSearchTerm] = useState('');

  // Filter States
  const [assessmentFilter, setAssessmentFilter] = useState('all'); // 'all' | 'completed' | 'not_completed'
  const [scoreFilter, setScoreFilter] = useState('all'); // 'all' | 'high70'

  // Candidate selection
  const [selectedCandidateIds, setSelectedCandidateIds] = useState(new Set());

  // --- PAGINATION STATES ---
  const [jobsPage, setJobsPage] = useState(1);
  const JOBS_PER_PAGE = 15;
  const [jobSearchTerm, setJobSearchTerm] = useState('');

  const [candidatesPage, setCandidatesPage] = useState(1);
  const CANDIDATES_PER_PAGE = 30;

  const fileInputRef = useRef(null);

  // --- STATUS PRIORITY MAP ---
  const statusPriority = {
    'Pending Review': 1,
    'Screening - Reject': 2, 'Screening - Select': 3,
    'Assessment - Reject': 4, 'Assessment - Select': 5,
    'Bot Review - Reject': 6, 'Bot Review - Select': 7,
    'L1 - Reject': 8, 'L1 - Select': 9,
    'L2 - Reject': 10, 'L2 - Select': 11,
    'L3 - Reject': 12, 'L3 - Select': 13,
    'L4 - Reject': 14, 'L4 - Select': 15,
    'Final Reject': 16, 'Final Select': 17,
    'On hold': 0
  };

  // --- EFFECTS ---
  useEffect(() => { fetchJobs(); }, []);

  useEffect(() => {
    // Only fetch stats for STANDARD jobs. Beeline jobs handle candidates in their own module.
    if ((viewState === 'SOURCES' || viewState === 'ALL_CANDIDATES') && selectedJob && !selectedJob.isBeeline) {
      fetchAndProcessJobData(selectedJob._id);
    }
  }, [viewState, selectedJob]);

  useEffect(() => {
    setSelectedCandidateIds(new Set());
    setCandidatesPage(1); 
    setSearchTerm(''); 
    setAssessmentFilter('all');
    setScoreFilter('all');
  }, [viewState, selectedJob, selectedSource]);

  useEffect(() => {
    setJobsPage(1);
  }, [activeTab, jobSearchTerm, isBeelineMode]);

  useEffect(() => {
    let filtered = [...allCandidates];

    // 1. Filter by Source
    if (viewState === 'APPLICANTS' && selectedSource) {
      filtered = filtered.filter(c => {
        if (selectedSource.type === 'VENDOR') {
          const cVid = c.vendorId?._id || c.vendorId;
          return cVid === selectedSource.id;
        } else if (selectedSource.type === 'RECRUITER') {
          const cRid = c.recruiterId?._id || c.recruiterId;
          return cRid === selectedSource.id;
        } else {
          const cCid = c.candidateUserId?._id || c.candidateUserId;
          return cCid === selectedSource.id;
        }
      });
    }

    // 2. Search Filter
    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        (c.candidateName && c.candidateName.toLowerCase().includes(lowerTerm)) ||
        (c.candidateEmail && c.candidateEmail.toLowerCase().includes(lowerTerm)) ||
        (c.contactNumber && c.contactNumber.includes(lowerTerm))
      );
    }

    // 2b. Assessment Status Filter
    if (assessmentFilter !== 'all') {
      filtered = filtered.filter(c => {
        const isDone = !!(c.assessmentSubmission &&
          c.assessmentSubmission.status === 'Completed' &&
          c.assessmentSubmission.overallScore != null);
        return assessmentFilter === 'completed' ? isDone : !isDone;
      });
    }

    // 2c. Score Filter (>= 70%)
    if (scoreFilter === 'high70') {
      filtered = filtered.filter(c => {
        const score = c.assessmentSubmission?.overallScore;
        return score != null && score >= 70;
      });
    }

    // 3. Sorting
    filtered.sort((a, b) => {
      if (sortOrder === 'match_desc') {
        const scoreA = Number.isFinite(a.matchScore) ? a.matchScore : -1;
        const scoreB = Number.isFinite(b.matchScore) ? b.matchScore : -1;
        if (scoreB !== scoreA) return scoreB - scoreA;
        const nameA = (a.candidateName || '').toLowerCase();
        const nameB = (b.candidateName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }
      const statusA = a.candidateStatus || 'Pending Review';
      const statusB = b.candidateStatus || 'Pending Review';
      const prioA = statusPriority[statusA] || 0;
      const prioB = statusPriority[statusB] || 0;
      if (sortOrder === 'status_asc') return prioA - prioB; 
      return prioB - prioA; 
    });

    setFilteredApplications(filtered);
  }, [viewState, selectedSource, allCandidates, sortOrder, searchTerm, assessmentFilter, scoreFilter]);

  // --- API FUNCTIONS ---
  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs`, {
        params: partnerId ? { partnerId } : {},
      });
      setJobs(response.data);
    } catch (error) { console.error("Error fetching jobs:", error); }
  };

  const fetchAndProcessJobData = async (jobId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/candidates/jobs/${jobId}/all-candidates`);
      let candidates = response.data;

      console.log('DEBUG partnerId:', partnerId);
      console.log('DEBUG candidate recruiterIds:', candidates.map(c => ({ name: c.candidateName, recruiterId: c.recruiterId, vendorId: c.vendorId })));

      // If viewing as a specific partner (vendor/recruiter), only show their own submissions
      if (partnerId) {
        candidates = candidates.filter(cand => {
          const vId = cand.vendorId?._id || cand.vendorId;
          const rId = cand.recruiterId?._id || cand.recruiterId;
          return vId === partnerId || rId === partnerId;
        });
      }

      setAllCandidates(candidates);

      const vMap = new Map();
      const rMap = new Map();
      const cMap = new Map();

      candidates.forEach(cand => {
        if (cand.vendorId) {
          const vid = cand.vendorId._id || cand.vendorId;
          const vName = (typeof cand.vendorId === 'object' && cand.vendorId.vendorName) ? cand.vendorId.vendorName : 'Unknown Vendor';
          if (!vMap.has(vid)) vMap.set(vid, { id: vid, name: vName, count: 0 });
          vMap.get(vid).count++;
        } else if (cand.recruiterId) {
          const rid = cand.recruiterId._id || cand.recruiterId;
          let rName = 'Unknown Recruiter';
          if (typeof cand.recruiterId === 'object') {
            rName = cand.recruiterId.recruiterName || cand.recruiterId.username || 'Unknown Recruiter';
          }
          if (!rMap.has(rid)) rMap.set(rid, { id: rid, name: rName, count: 0 });
          rMap.get(rid).count++;
        } else if (cand.candidateUserId) {
          const cid = cand.candidateUserId._id || cand.candidateUserId;
          let cName = 'Candidate Portal';
          if (typeof cand.candidateUserId === 'object') {
            cName = cand.candidateUserId.fullName || cand.candidateUserId.username || 'Candidate Portal';
          }
          if (!cMap.has(cid)) cMap.set(cid, { id: cid, name: cName, count: 0 });
          cMap.get(cid).count++;
        }
      });
      setStats({ vendors: Array.from(vMap.values()), recruiters: Array.from(rMap.values()), candidatePortal: Array.from(cMap.values()) });
    } catch (error) { console.error("Error processing job data:", error); }
  };

  // --- HANDLERS ---
    const handleUpdateCandidate = async (candidateId, field, value) => {
    try {
      const updateList = (list) => list.map(app => app._id === candidateId ? { ...app, [field]: value } : app);
      setAllCandidates(prev => updateList(prev));
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/candidates/${candidateId}`, { [field]: value });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      alert("Failed to save changes.");
    }
  };

  const handleToggleCandidateVisibility = async (job) => {
    try {
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/jobs/${job._id}`,
        { visibleToCandidates: !job.visibleToCandidates }
      );
      setJobs(prev => prev.map(j => j._id === job._id ? { ...j, visibleToCandidates: data.visibleToCandidates } : j));
    } catch (err) {
      alert('Failed to update candidate portal visibility.');
    }
  };

  const handleSortToggle = () => {
    setSortOrder(prev => {
      if (prev === 'match_desc') return 'status_desc';
      if (prev === 'status_desc') return 'status_asc';
      return 'match_desc';
    });
  };

  const toggleCandidateSelection = (id) => {
    setSelectedCandidateIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const areAllVisibleSelected =
    filteredApplications.length > 0 &&
    filteredApplications.every(c => selectedCandidateIds.has(c._id));

  const toggleSelectAllVisible = (checked) => {
    setSelectedCandidateIds(prev => {
      const next = new Set(prev);
      if (checked) {
        filteredApplications.forEach(c => next.add(c._id));
      } else {
        filteredApplications.forEach(c => next.delete(c._id));
      }
      return next;
    });
  };

  const getScoreColor = (s) => !s ? '#94a3b8' : s >= 80 ? '#16a34a' : s >= 50 ? '#d97706' : '#dc2626';

  const getFreshFileUrl = (url) => {
    if (!url) return '#';
    return `${process.env.REACT_APP_API_BASE_URL}/api/candidates/view-file?url=${encodeURIComponent(url)}`;
  };

  const handleExportExcel = () => {
    if (filteredApplications.length === 0) { alert("No data to export"); return; }

    const esc = (v) => {
      const s = (v === null || v === undefined) ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };

    const getSourceName = (app) => {
      if (app.vendorId) return (app.vendorId.vendorName || 'Vendor');
      if (app.recruiterId) return (app.recruiterId.recruiterName || app.recruiterId.username || 'Recruiter');
      return selectedSource?.name || '--';
    };

    const headers = [
      "Job Role", "Priority", "Match %", "Source", "Name", "Email", "Contact Number",
      "Total Experience", "Relevant Experience", "Present Organization",
      "Fixed CTC (Current)", "Expected CTC", "Work Status", "Notice Period",
      "Education", "Skills", "Status", "Profile Uploaded", "Comments",
      "Assessment Status", "Assessment Score (/10)"
    ];

    const rows = filteredApplications.map(app => {
      const match = (Number.isFinite(app.matchScore) && app.matchScore >= 0) ? `${app.matchScore}%` : '';

      const sub = app.assessmentSubmission;
      let assessmentStatus = 'Not Sent';
      let assessmentScore = '';
      if (sub) {
        if (sub.status === 'Completed') {
          if (sub.overallScore != null) {
            assessmentStatus = 'Completed';
            assessmentScore = (sub.overallScore / 10).toFixed(1);
          } else {
            assessmentStatus = 'Scoring';
          }
        } else {
          assessmentStatus = 'Pending';
        }
      }

      return [
        esc(selectedJob.role),
        esc(selectedJob.priority || 'P3'),
        esc(match),
        esc(getSourceName(app)),
        esc(app.candidateName || ''),
        esc(app.candidateEmail || ''),
        esc(app.contactNumber || ''),
        esc(app.totalExperience || '0'),
        esc(app.relevantExperience || '0'),
        esc(app.currentCompanyName || '--'),
        esc(app.currentCTC || ''),
        esc(app.expectedCTC || ''),
        esc(app.currentlyWorking || ''),
        esc(app.noticePeriod || ''),
        esc(app.education || '--'),
        esc(app.skillSet || '--'),
        esc(app.candidateStatus || 'Pending Review'),
        esc(app.profileUploaded || 'Yes'),
        esc(app.comments || ''),
        esc(assessmentStatus),
        esc(assessmentScore)
      ];
    });

    const csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(r => r.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Candidates_${selectedJob.role}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const triggerFileUpload = () => { fileInputRef.current.click(); };

  const handleProfileMatch = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsMatching(true);
    const formData = new FormData();
    formData.append('jdFile', file);
    formData.append('jobId', selectedJob._id);
    if (viewState === 'APPLICANTS' && selectedSource) {
      if (selectedSource.type === 'VENDOR') formData.append('vendorId', selectedSource.id);
      else formData.append('recruiterId', selectedSource.id);
    }
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/candidates/match-profiles`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data && response.data.updatedCandidates) {
        fetchAndProcessJobData(selectedJob._id);
        alert(`Match Complete!`);
      }
    } catch (error) { alert("Match failed."); } finally { setIsMatching(false); e.target.value = null; }
  };

  const isJobOpen = (job) => {
    if (!job.applyByDate) return true;
    const deadline = new Date(job.applyByDate);
    if (job.applyByTime) { const [h, m] = job.applyByTime.split(':'); deadline.setHours(Number(h || 23), Number(m || 59), 59, 999); }
    else { deadline.setHours(23, 59, 59, 999); }
    return new Date() <= deadline;
  };
  
  // --- FILTER JOBS (Open/Closed AND Standard/Beeline) ---
  // UPDATED: Now uses isBeelineMode (from props or local) to filter
  const filteredJobs = jobs.filter(job => {
    // 1. Filter by Tab (Open vs Closed)
    const openStatus = activeTab === 'open' ? isJobOpen(job) : !isJobOpen(job);
    
    // 2. Filter by Beeline Toggle (Using Lifted State Logic)
    const beelineStatus = isBeelineMode ? (job.isBeeline === true) : (!job.isBeeline);

    // 3. Filter by Search Term (company, title, location, beeline/req id)
    let searchStatus = true;
    if (jobSearchTerm.trim()) {
      const term = jobSearchTerm.toLowerCase();
      searchStatus = (
        (job.companyName && job.companyName.toLowerCase().includes(term)) ||
        (job.role && job.role.toLowerCase().includes(term)) ||
        (job.location && job.location.toLowerCase().includes(term)) ||
        (job.beelineId && job.beelineId.toLowerCase().includes(term)) ||
        (job.requestId && job.requestId.toLowerCase().includes(term))
      );
    }

    return openStatus && beelineStatus && searchStatus;
  });

  const formatDate = (d) => d && d.includes('T') ? d.split('T')[0] : '--';
  
  // --- UNIFIED CLICK HANDLER (UPDATED) ---
  // ALWAYS open the modal first for both types
  const handleJobClick = (job) => { 
    setSelectedJob(job); 
    setShowModal(true); 
  };
  
  const handleBack = () => {
    if (viewState === 'APPLICANTS' || viewState === 'ALL_CANDIDATES') { setViewState('SOURCES'); setFilteredApplications([]); setSearchTerm(''); }
    else if (viewState === 'SOURCES') { setViewState('LIST'); setStats({ vendors: [], recruiters: [], candidatePortal: [] }); setSelectedJob(null); }
  };
  
  const handleDelete = async (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!selectedJob || !selectedJob._id) return;
    if (window.confirm(`Delete "${selectedJob.role}"?`)) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/${selectedJob._id}`);
        setJobs(prev => prev.filter(j => j._id !== selectedJob._id));
        setShowModal(false); setSelectedJob(null); if (onDelete) onDelete(selectedJob._id);
      } catch (err) { alert(`Delete failed: ${err.message}`); }
    }
  };

  // --- SMART APPLICANT VIEWER (UPDATED) ---
  // This is triggered by the button IN the modal
  const handleViewApplicants = () => { 
    setShowModal(false); 
    
    if (selectedJob?.isBeeline && onViewBeeline) {
        onViewBeeline(selectedJob);
    } else {
        setViewState('SOURCES'); 
    }
  };

  const renderSkills = (skillsString) => {
    if (!skillsString) return <span style={{color:'#94a3b8'}}>--</span>;
    const skills = skillsString.split(/,|,\s|\n/).map(s => s.trim()).filter(Boolean);
    const visible = skills.slice(0, 4);
    const remaining = skills.length - visible.length;
    return (
        <div style={{display:'flex', flexWrap:'wrap', gap:'6px'}}>
            {visible.map((s,i) => (<span key={i} className="skill-pill">{s}</span>))}
            {remaining > 0 && (<span className="skill-more" title={skills.join(', ')}>+{remaining} More</span>)}
        </div>
    );
  };

  // Count of candidates with assessment score >= 70 (based on current source/search, ignoring the score filter)
const passedCount = allCandidates.filter(c => {
  const score = c.assessmentSubmission?.overallScore;
  const inSource =
    viewState === 'APPLICANTS' && selectedSource
      ? (selectedSource.type === 'VENDOR'
          ? (c.vendorId?._id || c.vendorId) === selectedSource.id
          : selectedSource.type === 'RECRUITER'
            ? (c.recruiterId?._id || c.recruiterId) === selectedSource.id
            : (c.candidateUserId?._id || c.candidateUserId) === selectedSource.id)
      : true;
  return inSource && score != null && score >= 70;
}).length;

  // --- PAGINATION LOGIC ---
  const indexOfLastJob = jobsPage * JOBS_PER_PAGE;
  const indexOfFirstJob = indexOfLastJob - JOBS_PER_PAGE;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalJobPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);

  const indexOfLastCand = candidatesPage * CANDIDATES_PER_PAGE;
  const indexOfFirstCand = indexOfLastCand - CANDIDATES_PER_PAGE;
  const currentCandidates = filteredApplications.slice(indexOfFirstCand, indexOfLastCand);
  const totalCandPages = Math.ceil(filteredApplications.length / CANDIDATES_PER_PAGE);

  // --- DYNAMIC HEADER RENDERER ---
  const renderHeader = () => {
    if (viewState === 'LIST') {
      return (
        <div className="header-left">
          <h1 className="page-title">{isBeelineMode ? "Beeline Requests (Tata)" : "Posted Jobs"}</h1>
          <div className="tabs-header" style={{padding:0, border:0, marginLeft:'20px'}}>
            <button className={`tab-btn ${activeTab === 'open' ? 'active' : ''}`} onClick={() => setActiveTab('open')}>Open</button>
            <button className={`tab-btn ${activeTab === 'closed' ? 'active' : ''}`} onClick={() => setActiveTab('closed')}>Closed</button>
          </div>

          {/* --- JOB SEARCH BAR --- */}
          <div className="search-bar-container" style={{marginLeft: 'auto', marginRight: '16px'}}>
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Search company, title, location..."
              value={jobSearchTerm}
              onChange={(e) => setJobSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* --- BEELINE TOGGLE (Updated Logic) --- */}
          <div className="beeline-toggle-wrapper" onClick={toggleBeeline}>
            <span className={!isBeelineMode ? "active-mode" : ""}>Standard</span>
            {isBeelineMode ? <ToggleRight size={32} color="#2563eb" /> : <ToggleLeft size={32} color="#94a3b8" />}
            <span className={isBeelineMode ? "active-mode" : ""}>Beeline</span>
          </div>
        </div>
      );
    } 
    
    // Sub-views
    let titleText = '';
    if (viewState === 'SOURCES') titleText = `Submissions: ${selectedJob?.role}`;
    else if (viewState === 'ALL_CANDIDATES') titleText = `All Candidates: ${selectedJob?.role}`;
    else if (viewState === 'APPLICANTS') titleText = `Candidates from ${selectedSource?.name}`;

    return (
      <>
        <div className="header-left">
            <button className="back-btn" onClick={handleBack} title="Go Back"><ArrowLeft size={18} /></button>
            <h1 className="page-title">{titleText}</h1>
        </div>

        {/* SEARCH & ACTIONS (Only for Candidate Views) */}
        {(viewState === 'APPLICANTS' || viewState === 'ALL_CANDIDATES') && (
            <div className="action-bar">
                <div className="search-bar-container">
                    <Search size={16} className="search-icon" />
                    <input 
                        type="text" 
                        placeholder="Search name, email..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

          <div
  style={{
    background: '#dcfce7',
    color: '#166534',
    border: '1px solid #bbf7d0',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  }}
  title="Candidates with assessment score ≥ 70%"
>
  {passedCount} Passed
</div>
      

                <select
                    value={scoreFilter}
                    onChange={(e) => setScoreFilter(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem', color: '#334155', background: '#fff' }}
                >
                    <option value="all">All Scores</option>
                    <option value="high70">Score ≥ 70%</option>
                </select>

                      <button
                        className="action-btn"
                        onClick={() => setShowComparisonModal(true)}
                        style={{ background: '#7c3aed', color: 'white', border: 'none' }}
                      >
                        Assessment Overview
                      </button>
                <button className="action-btn" onClick={handleExportExcel} style={{ background: '#10b981', color: 'white', border: 'none' }}><FileSpreadsheet size={16} /> Export</button>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleProfileMatch} accept=".pdf,.docx" />
                <button className="action-btn" onClick={triggerFileUpload} disabled={isMatching} style={{ background: isMatching ? '#94a3b8' : '#3b82f6', color: 'white', border: 'none' }}>{isMatching ? 'Matching...' : <><Zap size={16} /> AI Match</>}</button>
            </div>
        )}
      </>
    );
  };

  // --- RENDERERS ---
  const renderList = () => (
    <>
      <div className="table-container">
        <table className="modern-table clickable-rows">
          <thead>
            <tr>
              <th style={{ width: '80px' }}>Priority</th>
              <th>Company</th>
              <th>Title</th>
              {/* Conditional Column for Beeline ID */}
              {isBeelineMode && <th>Beeline / Req ID</th>}
              <th>Apply By</th>
              <th>Type</th>
              <th>CTC / Stipend</th>
              <th>Status</th>
              <th>Visit Date</th>
              <th>Candidate Portal</th>
            </tr>
          </thead>
          <tbody>
            {currentJobs.length > 0 ? currentJobs.map(job => (
            <tr key={job._id} onClick={() => handleJobClick(job)}>
              <td><span style={{ padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', backgroundColor: job.priority === 'P1' ? '#fee2e2' : job.priority === 'P2' ? '#ffedd5' : '#f1f5f9', color: job.priority === 'P1' ? '#991b1b' : job.priority === 'P2' ? '#9a3412' : '#64748b', border: `1px solid ${job.priority === 'P1' ? '#fecaca' : job.priority === 'P2' ? '#fed7aa' : '#e2e8f0'}` }}>{job.priority || 'P3'}</span></td>
              <td style={{ fontWeight: '600', color: '#1e293b' }}>{job.companyName}</td>
              <td style={{ color: '#2563eb', fontWeight: '500' }}>{job.role}</td>
              
              {/* Show Beeline ID if in Beeline Mode */}
              {isBeelineMode && (
                <td style={{fontSize:'0.85rem', color:'#64748b'}}>{job.beelineId || job.requestId || '--'}</td>
              )}

              <td><div className="date-cell"><Calendar size={14} /> {formatDate(job.applyByDate)}</div></td>
              <td>{job.typeOfWork}</td>
              <td>{job.ctc}</td>
              <td><span className={`status-badge ${isJobOpen(job) ? 'status-match' : 'status-rejected'}`}>{isJobOpen(job) ? 'Active' : 'Closed'}</span></td>
              <td>{formatDate(job.dateOfVisit) || 'TBD'}</td>
              <td onClick={(e) => e.stopPropagation()}>
                <button
                  className="action-btn"
                  style={{
                    background: job.visibleToCandidates ? '#16a34a' : '#e2e8f0',
                    color: job.visibleToCandidates ? 'white' : '#334155',
                    border: 'none', fontSize: '0.75rem', padding: '5px 10px'
                  }}
                  onClick={() => handleToggleCandidateVisibility(job)}
                >
                  {job.visibleToCandidates ? 'Live for Candidates' : 'Publish to Candidates'}
                </button>
              </td>
            </tr>
            )) : <tr><td colSpan={isBeelineMode ? "10" : "9"} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No {activeTab} {isBeelineMode ? "Beeline" : "standard"} jobs found.</td></tr>}
          </tbody>
        </table>
      </div>
      
      {/* JOBS PAGINATION CONTROLS */}
      <div className="pagination-footer">
          <span style={{color:'#64748b', fontSize:'0.9rem'}}>
            Showing {filteredJobs.length > 0 ? indexOfFirstJob + 1 : 0}-{Math.min(indexOfLastJob, filteredJobs.length)} of {filteredJobs.length} jobs
          </span>
          <div style={{display:'flex', gap:'10px'}}>
              <button disabled={jobsPage === 1} onClick={() => setJobsPage(p => p - 1)} className="pg-btn"><ChevronLeft size={16}/> Prev</button>
              <button disabled={jobsPage === totalJobPages || totalJobPages === 0} onClick={() => setJobsPage(p => p + 1)} className="pg-btn">Next <ChevronRight size={16}/></button>
          </div>
      </div>
    </>
  );

  const renderSources = () => (
    <div style={{flex: 1, overflow:'auto', padding:'20px'}}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 className="section-heading" style={{ margin: 0 }}>Submissions for {selectedJob.role}</h3>
        {!partnerId && (
          <button onClick={() => setViewState('ALL_CANDIDATES')} style={{ background: '#1e293b', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}><Users size={16} /> View All Candidates</button>
        )}
      </div>
      <h4 style={{ color: '#64748b', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}><Building2 size={18} /> Vendor Submissions</h4>
      <div className="list-grid" style={{ marginBottom: '30px' }}>
        {stats.vendors.length > 0 ? stats.vendors.map(v => (<div key={v.id} className="vendor-card" onClick={() => { setSelectedSource({ ...v, type: 'VENDOR' }); setViewState('APPLICANTS'); }}><div className="vc-icon"><Briefcase size={20} /></div><div className="vc-info"><h4>{v.name}</h4><span>{v.count} Applications</span></div><ChevronRight className="vc-arrow" /></div>)) : <div style={{ color: '#94a3b8', fontStyle: 'italic', padding: '10px' }}>No vendor submissions yet.</div>}
      </div>
      <h4 style={{ color: '#64748b', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}><UserPlus size={18} /> Recruiter Submissions</h4>
      <div className="list-grid" style={{ marginBottom: '30px' }}>
        {stats.recruiters.length > 0 ? stats.recruiters.map(r => (<div key={r.id} className="vendor-card" onClick={() => { setSelectedSource({ ...r, type: 'RECRUITER' }); setViewState('APPLICANTS'); }}><div className="vc-icon" style={{ background: '#f3e8ff', color: '#7c3aed' }}><Users size={20} /></div><div className="vc-info"><h4>{r.name}</h4><span>{r.count} Applications</span></div><ChevronRight className="vc-arrow" /></div>)) : <div style={{ color: '#94a3b8', fontStyle: 'italic', padding: '10px' }}>No recruiter submissions yet.</div>}
      </div>
      <h4 style={{ color: '#64748b', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}><User size={18} /> Applied via Candidate Portal</h4>
      <div className="list-grid">
        {stats.candidatePortal.length > 0 ? stats.candidatePortal.map(cp => (<div key={cp.id} className="vendor-card" onClick={() => { setSelectedSource({ ...cp, type: 'CANDIDATE' }); setViewState('APPLICANTS'); }}><div className="vc-icon" style={{ background: '#fff7ed', color: '#ea580c' }}><User size={20} /></div><div className="vc-info"><h4>{cp.name}</h4><span>{cp.count} Applications</span></div><ChevronRight className="vc-arrow" /></div>)) : <div style={{ color: '#94a3b8', fontStyle: 'italic', padding: '10px' }}>No direct candidate applications yet.</div>}
      </div>
    </div>
  );

  const renderApplicantsTable = (isAllView) => (
    <>
      <div style={{ flex: 1, overflow: 'auto', position:'relative' }} className="table-container">
        <table className="modern-table" style={{ minWidth: '3400px' }}>
          <thead>
            <tr>
              <th style={{ width: '50px', textAlign: 'center' }}><input type="checkbox" checked={areAllVisibleSelected} onChange={(e) => toggleSelectAllVisible(e.target.checked)} /></th>
              {/* STICKY COLUMN: NAME (Moved to left as requested) */}
              <th className="sticky-name" style={{ width: '220px' }}>Candidate Name</th>
              
              <th style={{ width: '150px' }}>Assessment</th> 
              
              {/* RESTORED COLUMNS */}
              <th style={{ width: '150px' }}>Job Role</th>
              <th style={{ width: '80px' }}>Priority</th>
              <th style={{ width: '80px' }}>Match</th>
              <th style={{ width: '150px' }}>Source</th>
              
              <th style={{ width: '200px' }}>Email</th>
              <th style={{ width: '120px' }}>Contact Number</th>
              <th style={{ width: '100px' }}>Total Exp</th>
              <th style={{ width: '100px' }}>Rel Exp</th>
              <th style={{ width: '180px' }}>Present Organization</th>
              <th style={{ width: '120px' }}>Fixed CTC (Current)</th>
              <th style={{ width: '120px' }}>Expected CTC</th>
              <th style={{ width: '120px' }}>Work Status</th>
              <th style={{ width: '120px' }}>Notice Period</th>
              <th style={{ width: '350px' }}>Skills</th> {/* Increased width for pills */}
              <th style={{ width: '250px' }}>Education</th>
              <th style={{ width: '100px' }}>Resume</th>
              <th style={{ width: '180px' }}>Status</th>
              <th style={{ width: '150px' }}>Profile Uploaded</th>
              <th style={{ width: '250px' }}>Comments</th>
            </tr>
          </thead>
          <tbody>
            {currentCandidates.length > 0 ? currentCandidates.map(app => (
              <tr key={app._id} style={{borderBottom:'1px solid #f1f5f9'}}>
                <td style={{ textAlign: 'center' }}><input type="checkbox" checked={selectedCandidateIds.has(app._id)} onChange={() => toggleCandidateSelection(app._id)} /></td>
                
               {/* STICKY CELL: NAME */}
              <td
                className="sticky-name"
                style={{
                  fontWeight: '600',
                  color: '#1e293b',
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  verticalAlign: 'top',
                  paddingTop: '14px',
                  paddingBottom: '14px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', whiteSpace: 'normal' }}>
                  <span>{app.candidateName}</span>
                  <TriggerAssessmentButton
                    candidateId={app._id}
                    alreadySent={!!app.assessmentSubmission}
                    attemptCount={app.assessmentAttemptCount || 0}
                  />
                </div>
              </td>

              
                <td>
                  {app.assessmentSubmission ? (
                    app.assessmentSubmission.status === 'Completed' ? (
                      app.assessmentSubmission.overallScore != null ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                          <span style={{ fontWeight: 'bold', color: getScoreColor(app.assessmentSubmission.overallScore) }}>
                            {(app.assessmentSubmission.overallScore / 10).toFixed(1)}/10
                          </span>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => setViewingSubmissionId(app.assessmentSubmission.id)}
                              style={{
                                background: '#eff6ff', color: '#2563eb', border: 'none',
                                borderRadius: '4px', padding: '4px 8px', fontSize: '0.75rem',
                                fontWeight: 600, cursor: 'pointer'
                              }}
                            >
                              View Report
                            </button>
                            <button
                              onClick={async () => {
                                try { await downloadAssessmentReport(app, selectedJob.role); }
                                catch (e) { console.error(e); alert('Failed: ' + (e.response?.status || '') + ' ' + (e.config?.url || e.message)); }
                              }}
                              style={{
                                background: '#f0fdf4', color: '#166534', border: 'none',
                                borderRadius: '4px', padding: '4px 8px', fontSize: '0.75rem',
                                fontWeight: 600, cursor: 'pointer'
                              }}
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#d97706', fontSize: '0.8rem' }}>Scoring...</span>
                      )
                    ) : (
                      <span style={{ color: '#64748b', fontSize: '0.8rem' }}>Pending</span>
                    )
                  ) : (
                    <span style={{ color: '#cbd5e1' }}>--</span>
                  )}
                </td>

                {/* RESTORED COLUMNS DATA */}
                <td><span className="badge-info" style={{fontSize:'0.85rem'}}>{selectedJob.role}</span></td>
                <td><span style={{ padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', backgroundColor: selectedJob.priority === 'P1' ? '#fee2e2' : selectedJob.priority === 'P2' ? '#ffedd5' : '#f1f5f9', color: selectedJob.priority === 'P1' ? '#991b1b' : selectedJob.priority === 'P2' ? '#9a3412' : '#64748b', border: `1px solid ${selectedJob.priority === 'P1' ? '#fecaca' : selectedJob.priority === 'P2' ? '#fed7aa' : '#e2e8f0'}` }}>{selectedJob.priority || 'P3'}</span></td>
                <td>{app.matchScore > 0 ? <div style={{ fontWeight: 'bold', color: getScoreColor(app.matchScore) }}>{app.matchScore}%</div> : <span style={{ color: '#94a3b8' }}>--</span>}</td>
                <td>
                  {app.vendorId ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#475569', fontSize: '0.9rem' }}><Building2 size={14} color="#059669" /><span>{app.vendorId.vendorName}</span></div>
                  ) : app.recruiterId ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#475569', fontSize: '0.9rem' }}><Briefcase size={14} color="#7c3aed" /><span>{(app.recruiterId.recruiterName || app.recruiterId.username || 'Recruiter')}</span></div>
                  ) : app.candidateUserId ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#475569', fontSize: '0.9rem' }}><User size={14} color="#ea580c" /><span>Candidate Portal</span></div>
                  ) : '--'}
                </td>

                <td className="text-muted" style={{ fontSize: '0.85rem' }}>{app.candidateEmail}</td>
                <td>{app.contactNumber || '--'}</td>
                <td style={{ fontWeight: '500' }}>{app.totalExperience || '0'}</td>
                <td style={{ color: '#64748b' }}>{app.relevantExperience || '0'}</td>
                <td style={{ fontWeight: '500' }}>{app.currentCompanyName || '--'}</td>
                <td>{app.currentCTC || '--'}</td>
                <td>{app.expectedCTC || '--'}</td>
                <td>{app.currentlyWorking || '--'}</td>
                <td style={{ color: '#d97706', fontWeight: '500' }}>{app.noticePeriod || '--'}</td>
                
                {/* SKILLS RENDERER */}
                <td>{renderSkills(app.skillSet)}</td>

                <td><div style={{ whiteSpace: 'normal', lineHeight: '1.5', fontSize: '0.85rem', color: '#334155' }}>{app.education || '--'}</div></td>
                <td>{app.resumeFile && app.resumeFile.fileUrl ? (<a href={getFreshFileUrl(app.resumeFile.fileUrl)} target="_blank" rel="noreferrer" style={{ color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem', fontWeight: '600' }}><Download size={14} /> PDF</a>) : <span style={{ color: '#cbd5e1' }}>--</span>}</td>

                <td>
                  <select className="status-dropdown" value={app.candidateStatus || 'Pending Review'} onChange={(e) => handleUpdateCandidate(app._id, 'candidateStatus', e.target.value)} style={{ width: '100%', fontSize: '0.85rem' }}>
                    <option value="Pending Review">Pending Review</option>
                    <optgroup label="Screening"><option value="Screening - Select">Screening - Select</option><option value="Screening - Reject">Screening - Reject</option></optgroup>
                    <optgroup label="Assessment"><option value="Assessment - Select">Assessment - Select</option><option value="Assessment - Reject">Assessment - Reject</option></optgroup>
                    <optgroup label="Bot Review"><option value="Bot Review - Select">Bot Review - Select</option><option value="Bot Review - Reject">Bot Review - Reject</option></optgroup>
                    <optgroup label="Level 1"><option value="L1 - Select">L1 - Select</option><option value="L1 - Reject">L1 - Reject</option></optgroup>
                    <optgroup label="Level 2"><option value="L2 - Select">L2 - Select</option><option value="L2 - Reject">L2 - Reject</option></optgroup>
                    <optgroup label="Level 3"><option value="L3 - Select">L3 - Select</option><option value="L3 - Reject">L3 - Reject</option></optgroup>
                    <optgroup label="Level 4"><option value="L4 - Select">L4 - Select</option><option value="L4 - Reject">L4 - Reject</option></optgroup>
                    <optgroup label="Final"><option value="Final Select">Final Select</option><option value="Final Reject">Final Reject</option><option value="On hold">On hold</option></optgroup>
                  </select>
                </td>



                <td>
                  <select className="status-dropdown" value={app.profileUploaded || 'Yes'} onChange={(e) => handleUpdateCandidate(app._id, 'profileUploaded', e.target.value)} style={{ width: '100%', fontSize: '0.85rem' }}>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </td>

                <td>
                  {app.profileUploaded === 'No' ? (
                    <input type="text" defaultValue={app.comments || ''} onBlur={(e) => handleUpdateCandidate(app._id, 'comments', e.target.value)} placeholder="Why is profile missing?" className="comment-input" style={{ width: '100%', padding: '6px', borderColor: '#ef4444' }} />
                  ) : (
                    <input type="text" defaultValue={app.comments || ''} onBlur={(e) => handleUpdateCandidate(app._id, 'comments', e.target.value)} placeholder="Add note..." className="comment-input" style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #e2e8f0' }} />
                  )}
                </td>
              </tr>
            )) : (
              <tr><td colSpan={21} style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>No applications found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* CANDIDATE PAGINATION */}
      <div className="pagination-footer">
          <span style={{color:'#64748b', fontSize:'0.9rem'}}>
            Showing {filteredApplications.length > 0 ? indexOfFirstCand + 1 : 0}-{Math.min(indexOfLastCand, filteredApplications.length)} of {filteredApplications.length} candidates
          </span>
          <div style={{display:'flex', gap:'10px'}}>
              <button disabled={candidatesPage === 1} onClick={() => setCandidatesPage(p => p - 1)} className="pg-btn"><ChevronLeft size={16}/> Prev</button>
              <button disabled={candidatesPage === totalCandPages || totalCandPages === 0} onClick={() => setCandidatesPage(p => p + 1)} className="pg-btn">Next <ChevronRight size={16}/></button>
          </div>
      </div>
    </>
  );

  const JobDetailsModal = () => {
    if (!showModal || !selectedJob) return null;
    const fileUrl = selectedJob.fileUrl || selectedJob.localJdFile;
const finalLink = fileUrl ? getFreshFileUrl(fileUrl) : '#';
    return (
      <div className="modal-overlay" onClick={() => setShowModal(false)}>
        <div className="modal-content" style={{ maxWidth: '900px', width: '95%' }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <div>
              <h2>{selectedJob.role}</h2>
              <p>{selectedJob.companyName} • {selectedJob.location} • {selectedJob.typeOfWork}</p>
              
              {/* --- SHOW BEELINE INFO IN MODAL --- */}
              {selectedJob.isBeeline && (
                <div style={{display:'flex', gap:'12px', marginTop:'8px'}}>
                    <span style={{background:'#eff6ff', color:'#2563eb', padding:'4px 8px', borderRadius:'6px', fontSize:'0.75rem', fontWeight:'600', display:'flex', alignItems:'center', gap:'4px'}}>
                        <Hash size={12}/> Req ID: {selectedJob.requestId}
                    </span>
                    <span style={{background:'#f0fdf4', color:'#166534', padding:'4px 8px', borderRadius:'6px', fontSize:'0.75rem', fontWeight:'600', display:'flex', alignItems:'center', gap:'4px'}}>
                        <Zap size={12}/> Beeline ID: {selectedJob.beelineId}
                    </span>
                </div>
              )}
            </div>
            <button className="close-btn" onClick={() => setShowModal(false)}><X size={20} /></button>
          </div>
          <div className="modal-body">
            <div className="detail-section">
              <div className="info-grid-3">
                <div className="info-item"><label>CTC</label><span style={{ color: '#059669' }}>{selectedJob.ctc}</span></div>
                <div className="info-item"><label>Batch</label><span>{selectedJob.batch || 'Any'}</span></div>

                <div className="info-item">
                  <label>Apply By</label>
                  <span style={{ color: '#dc2626' }}>
                    {formatDate(selectedJob.applyByDate)}
                    {selectedJob.applyByTime ? ` • ${selectedJob.applyByTime}` : ''}
                  </span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4><CheckCircle size={14} /> Eligibility Criteria</h4>
              <div className="info-grid-3" style={{ rowGap: '24px' }}>
                <div className="info-item"><label>10th Percentage</label><span>{selectedJob.tenth || '--'}%</span></div>
                <div className="info-item"><label>12th Percentage</label><span>{selectedJob.twelfth || '--'}%</span></div>
                <div className="info-item"><label>Min CGPA</label><span>{selectedJob.minCGPA || '--'}</span></div>
                <div className="info-item"><label>Passout Year</label><span>{selectedJob.passoutYear || 'Any'}</span></div>
                <div className="info-item"><label>Allowed Gender</label><span>{selectedJob.gender}</span></div>
                <div className="info-item"><label>Backlogs (History/Current)</label><span>{selectedJob.maxHistoryArrears} / {selectedJob.maxCurrentArrears}</span></div>
              </div>
            </div>

            <div className="detail-section"><h4>About The Job</h4><div className="text-block">{selectedJob.aboutJob || 'No description provided.'}</div></div>
            {selectedJob.aboutCompany && (<div className="detail-section"><h4>About Company</h4><div className="text-block">{selectedJob.aboutCompany}</div></div>)}
            {selectedJob.hiringWorkflows && (<div className="detail-section"><h4>Hiring Workflow</h4><div className="text-block">{selectedJob.hiringWorkflows}</div></div>)}

            {fileUrl && (
              <div className="detail-section">
                <h4><FileText size={16} style={{ marginBottom: '-2px' }} /> Attached JD</h4>
                <div style={{ background: '#f1f5f9', padding: '12px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.9rem', color: '#334155' }}>Job Description Document</span>
                  <a href={finalLink} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.9rem', fontWeight: '500' }}>
                    <Download size={16} /> Download
                  </a>
                </div>
              </div>
            )}

            {(selectedJob.externalLink || selectedJob.additionalInfo) && (
              <div className="detail-section" style={{ marginBottom: 0 }}>
                <h4>Additional Info</h4>
                {selectedJob.externalLink && (<a href={selectedJob.externalLink} target="_blank" rel="noreferrer" className="external-link-box"><Info size={18} /> Apply Externally: {selectedJob.externalLink}</a>)}
                {selectedJob.additionalInfo && (<div style={{ marginTop: '12px', fontSize: '0.9rem', color: '#64748b', fontStyle: 'italic' }}>Note: {selectedJob.additionalInfo}</div>)}
              </div>
            )}
          </div>

          <div className="modal-footer" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '8px' }}>
            {!partnerId && selectedJob.isBeeline && (
              <button className="action-btn" onClick={() => setShowExisting(true)} style={{ background: '#7c3aed', color: '#fff', border: 'none' }}>
                <Database size={16} /> Existing Candidates
              </button>
            )}
            {!partnerId && (
              <>
                <button className="action-btn edit" onClick={() => { setShowModal(false); onEdit && onEdit(selectedJob); }}><Edit2 size={16} /> Edit Job</button>
                <button type="button" className="action-btn delete" onClick={handleDelete}><Trash2 size={16} /> Delete</button>
              </>
            )}
            <button className="action-btn" onClick={() => setShowPreviewModal(true)} style={{ background: '#0891b2', color: '#fff', border: 'none' }}>
              <FileText size={16} /> View Assessment
            </button>
            {/* --- SMART VIEW BUTTON --- */}
            <button className="action-btn view-apps" onClick={handleViewApplicants}>
                <Users size={16} /> {selectedJob.isBeeline ? "View Submissions" : "View Applicants"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="posted-jobs-wrapper">
      <div className="page-header">
        {/* DYNAMIC HEADER CONTENT */}
        {renderHeader()}
      </div>
      
      {viewState === 'LIST' && renderList()}
      {viewState === 'SOURCES' && renderSources()}
      {viewState === 'APPLICANTS' && renderApplicantsTable(false)}
      {viewState === 'ALL_CANDIDATES' && renderApplicantsTable(true)}
      
      <JobDetailsModal />
      <AssessmentReportModal
  submissionId={viewingSubmissionId}
  onClose={() => setViewingSubmissionId(null)}
/>
{showComparisonModal && (
  <AssessmentComparisonModal
    jobId={selectedJob._id}
    jobRole={selectedJob.role}
    onClose={() => setShowComparisonModal(false)}
  />
)}
{showPreviewModal && (
  <AssessmentPreviewModal
    jobId={selectedJob._id}
    jobRole={selectedJob.role}
    onClose={() => setShowPreviewModal(false)}
  />
)}
{showExisting && selectedJob && (
  <ExistingCandidatesModal
    job={selectedJob}
    onSelectCandidate={(p) => { setAdminPrefill(p); setShowExisting(false); setShowModal(false); }}
    onClose={() => setShowExisting(false)}
  />
)}

{adminPrefill && selectedJob && (
  <div style={{ position: 'fixed', inset: 0, background: '#f8fafc', zIndex: 9999, overflowY: 'auto' }}>
    <BeelineSubmissionForm
      job={selectedJob}
      partner={{ role: 'admin' }}
      prefillData={adminPrefill}
      onBack={() => setAdminPrefill(null)}
    />
  </div>
)}
    </div>
  );
}